/**
 * Client ids and originator stamps for queued creates (migration plan P2-05,
 * architecture §J "Bridge Phase 2").
 *
 * Every task, board post, supply request and shelter check-in created on the
 * bridge carries:
 *  - `client_id`: a UUID generated on the device and sent to the server as the
 *    row's `id`. The server's create is insert-or-return on that id, so a
 *    replay whose first response was lost (timeout, app killed mid-request,
 *    radio drop) returns the existing row instead of creating a duplicate.
 *  - originator stamps: the user, tenant and device session that created the
 *    item, and when. They keep items from different people on a shared device
 *    separate (they are never replayed as whoever signed in next) and they are
 *    what the drain hands to the native app later (plan §6.2 "Item stamps").
 *
 * The stamps are plain (non-indexed) properties, so no Dexie version bump.
 */
import { decodeJwtPayload, useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { parseApiError } from '@/utils/api-errors';
import type { OriginatorStamp } from './db';
import { setOriginProvider, UNKNOWN_ORIGINATOR } from './drain/drain-write-tracker';
import { rowTenantsOf } from './row-tenant';

export type { OriginatorStamp };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function newClientId(): string {
  return crypto.randomUUID();
}

function currentUserId(): string | null {
  try {
    return useAuthStore.getState().user?.id ?? null;
  } catch {
    return null;
  }
}

function currentDeviceSessionId(): string | null {
  try {
    const token = useAuthStore.getState().token;
    if (!token) return null;
    const sid = decodeJwtPayload(token)?.sid;
    return typeof sid === 'string' && sid.length > 0 ? sid : null;
  } catch {
    return null;
  }
}

function activeTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

/** Who is signed in now, in which tenant, on which device session (all null when unknown). */
export function currentOriginator(): { userId: string | null; tenantId: string | null; deviceSessionId: string | null } {
  return { userId: currentUserId(), tenantId: activeTenantId(), deviceSessionId: currentDeviceSessionId() };
}

// Every unsynced Dexie write is stamped with its originator by the database hooks
// (drain-write-tracker.ts); this module tells them who is signed in.
setOriginProvider(currentOriginator);

/**
 * Only the originator part of a stamp (who, in which tenant, on which device
 * session), for items that already have their own client id.
 */
export function originOnly(tenantId?: string | null): Pick<OriginatorStamp, 'origin_user_id' | 'origin_tenant_id' | 'origin_device_session_id'> {
  return { origin_user_id: currentUserId(), origin_tenant_id: tenantId ?? activeTenantId(), origin_device_session_id: currentDeviceSessionId() };
}

/** Why a stored row must not be sent under the current session (null: it may be). */
export type SendDeferral = 'unknown_originator' | 'another_user' | 'no_session' | 'no_tenant' | 'another_tenant';

/**
 * THE originator gate (contract 9): the one decision every network send of a stored
 * offline row goes through (single-item sync, retry, photo/attachment upload, syncAll
 * sweeps; pinned by originatorGate.contract.spec.ts). A row is deferred, and left
 * pending for its owner or the drain's handoff, when it was:
 *  - stamped UNKNOWN_ORIGINATOR (it became pending while nobody was signed in);
 *  - stamped by another user, or stamped while nobody is signed in now;
 *  - stamped for, or stored in, another tenant than the active one;
 *  - anything at all while NO tenant is active: the api client then sends no X-Tenant-ID
 *    and the server falls back to the token's default tenant, so nothing is ever sent
 *    without an explicit tenant (e.g. after MEMBERSHIP_REMOVED cleared the active tenant);
 *  - stamped with no tenant at all (written while no tenant was active): its tenant is
 *    unknown, so it goes with the handoff (ORIGIN_INCOMPLETE), never under the active one.
 * Unstamped rows (queued by older builds) keep the old behaviour within the active tenant.
 */
export function sendDeferral(item: OriginatorStamp & { tenant_id?: unknown }): SendDeferral | null {
  if (item.origin_user_id === UNKNOWN_ORIGINATOR) return 'unknown_originator';
  const me = currentUserId();
  if (item.origin_user_id && !me) return 'no_session';
  if (item.origin_user_id && me && item.origin_user_id !== me) return 'another_user';
  const tenant = activeTenantId();
  if (!tenant) return 'no_tenant';
  // Every place the row names a tenant must be the active one (row-tenant.ts, shared with the
  // tenant wipe): its stamp, its tenant_id, a cached server copy's data.tenant_id.
  const tenants = rowTenantsOf(item);
  if (tenants.some((t) => t !== tenant)) return 'another_tenant';
  if (item.origin_user_id && tenants.length === 0) return 'no_tenant';
  return null;
}

/**
 * The tenant a stored row is sent under (round 7 L1), as the api SendConfig: the row's own
 * tenant (row-tenant.ts), else (an unstamped row of an older build) the active one. Every gated
 * send passes it, so X-Tenant-ID is pinned to the row and a tenant switch between the gate and
 * the request cannot retarget it (originatorGate.contract.spec.ts checks every send passes it).
 */
export function sendTenantOf(item: unknown): { tenantId: string } {
  return { tenantId: rowTenantsOf(item)[0] ?? activeTenantId() ?? '' };
}

/** True when the row must not be sent now (see sendDeferral). Call it before every send. */
export function mustDeferSend(item: OriginatorStamp & { tenant_id?: unknown }): boolean {
  return sendDeferral(item) !== null;
}

/** The same gate, under its older name (list filters). */
export function ownedByAnotherOriginator(item: OriginatorStamp & { tenant_id?: unknown }): boolean {
  return mustDeferSend(item);
}

/** Stamp a new item with its client id and originator, at creation time. */
export function originatorStamp(tenantId?: string | null, clientId: string = newClientId()): Required<OriginatorStamp> {
  return {
    client_id: clientId,
    origin_user_id: currentUserId(),
    origin_tenant_id: tenantId ?? activeTenantId(),
    origin_device_session_id: currentDeviceSessionId(),
    occurred_at: new Date().toISOString(),
  };
}

/**
 * The UUID inside an `offline_<uuid>` local id. Items queued before client ids
 * existed already hold a device-generated UUID there, so it doubles as their
 * client id and their replays become idempotent too.
 */
export function clientIdFromOfflineId(offlineId: string | null | undefined): string | undefined {
  if (!offlineId) return undefined;
  const candidate = offlineId.startsWith('offline_') ? offlineId.slice('offline_'.length) : offlineId;
  return UUID_RE.test(candidate) ? candidate.toLowerCase() : undefined;
}

/** The client id to send for an item: its own, else the one in its offline id. */
export function clientIdFor(item: OriginatorStamp & { offline_id?: string | null }): string | undefined {
  if (item.client_id && UUID_RE.test(item.client_id)) return item.client_id;
  return clientIdFromOfflineId(item.offline_id);
}

/**
 * True when the item was created by someone other than the signed-in user.
 * Such an item is deferred, never replayed under the current user (it would be
 * attributed to the wrong person, or refused as a client-id conflict). Items
 * without a stamp (queued by older builds) are not deferred. An item stamped
 * UNKNOWN_ORIGINATOR (it became pending while nobody was signed in) is nobody's
 * to send: always deferred, and the drain hands it off for quarantine.
 */
export function belongsToAnotherUser(item: OriginatorStamp): boolean {
  if (item.origin_user_id === UNKNOWN_ORIGINATOR) return true;
  const me = currentUserId();
  return !!item.origin_user_id && !!me && item.origin_user_id !== me;
}

/** HTTP status and API error code of a failed request, if any. */
export function httpFailure(error: unknown): { status?: number; code?: string } {
  let status = (error as { response?: { status?: number } })?.response?.status;
  let code: string | undefined;
  try {
    const parsed = parseApiError(error);
    code = parsed.code;
    status ??= parsed.status;
  } catch {
    code = undefined;
  }
  return { status, code };
}

/**
 * The server answered a create replay with "this record was created earlier
 * and has since been deleted" (410 RECORD_DELETED). The create did land, so
 * the local item is done; it must not be retried or re-created.
 */
export function isDeletedReplay(error: unknown): boolean {
  const { status, code } = httpFailure(error);
  return status === 410 || code === 'RECORD_DELETED';
}

/**
 * Error codes meaning "the state this mutation sets is already the server's
 * state": the replay is a success, not a failure (P2-05).
 */
export const ALREADY_APPLIED_CODES: readonly string[] = ['ALREADY_CHECKED_OUT'];

/** A 409 whose code says the change is already in effect (e.g. ALREADY_CHECKED_OUT). */
export function isAlreadyAppliedReplay(error: unknown): boolean {
  const { status, code } = httpFailure(error);
  return status === 409 && code !== undefined && ALREADY_APPLIED_CODES.includes(code);
}

/** The client id is held by someone else's record (409 CLIENT_ID_CONFLICT). Never "already synced". */
export function isClientIdConflict(error: unknown): boolean {
  return httpFailure(error).code === 'CLIENT_ID_CONFLICT';
}
